import { useState } from "react";
import { autoResponderSources, type AutoResponderSource } from "@/mocks/workflowsData";

interface Props {
  masterEnabled: boolean;
  onMasterToggle: (enabled: boolean) => void;
  sources: AutoResponderSource[];
  onSourcesChange: (sources: AutoResponderSource[]) => void;
}

export default function AutoResponderSection({ masterEnabled, onMasterToggle, sources, onSourcesChange }: Props) {
  const toggleSource = (id: string) => {
    onSourcesChange(sources.map((s) => (s.id === id && !s.locked ? { ...s, enabled: !s.enabled } : s)));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
      {/* Top Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-2-line text-lg text-indigo-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Auto-Responder</h3>
            <p className="text-sm text-gray-500 mt-0.5 max-w-md">Automatically engage leads via WhatsApp before agent assignment</p>
          </div>
        </div>

        {/* Master Toggle */}
        <button
          onClick={() => onMasterToggle(!masterEnabled)}
          className={`relative inline-flex items-center h-8 w-16 rounded-full transition-all duration-300 flex-shrink-0 cursor-pointer ${
            masterEnabled ? "bg-indigo-600" : "bg-gray-200"
          }`}
        >
          <span
            className={`inline-block w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform duration-300 ${
              masterEnabled ? "translate-x-9" : "translate-x-1"
            }`}
          />
          <span
            className={`absolute text-xs font-medium transition-opacity duration-200 whitespace-nowrap ${
              masterEnabled ? "opacity-0" : "opacity-100 left-[-60px] text-gray-500"
            }`}
          >
            Disabled
          </span>
          <span
            className={`absolute text-xs font-medium transition-opacity duration-200 whitespace-nowrap ${
              masterEnabled ? "opacity-100 right-[-50px] text-indigo-600" : "opacity-0"
            }`}
          >
            Active
          </span>
        </button>
      </div>

      {/* Status Info Box */}
      {!masterEnabled ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-amber-800 text-sm">Auto-responder is disabled</p>
            <p className="text-sm text-amber-700 mt-0.5">
              All new leads will be assigned directly to agents using your assignment rules. Enable to activate WhatsApp bot qualification.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-green-600" />
          </div>
          <div>
            <p className="font-medium text-green-800 text-sm">Auto-responder is active</p>
            <p className="text-sm text-green-700 mt-0.5">
              New leads will receive automatic WhatsApp messages before agent assignment. Leads must qualify before being assigned.
            </p>
          </div>
        </div>
      )}

      {/* Per-Source Toggles (shown when master ON) */}
      {masterEnabled && (
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Enable auto-responder for:</p>
          <div className="divide-y divide-gray-100">
            {sources.map((src) => (
              <div key={src.id} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${src.color}`}>
                    {src.name}
                  </span>
                  <span className="text-xs text-gray-400 truncate">{src.helper}</span>
                </div>
                <button
                  onClick={() => toggleSource(src.id)}
                  disabled={src.locked}
                  className={`relative inline-flex items-center h-6 w-11 rounded-full transition-all duration-200 flex-shrink-0 ${
                    src.locked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                  } ${src.enabled ? "bg-indigo-600" : "bg-gray-200"}`}
                >
                  <span
                    className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                      src.enabled ? "translate-x-6" : "translate-x-0.5"
                    }`}
                  />
                  {src.locked && (
                    <i className="ri-lock-line text-[8px] text-gray-400 absolute right-0.5" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}